// src/utils/mobile.js
//
// One mobile-number rule for every mobile / WhatsApp field (24 Sep 2026),
// per COUNTRY. The table itself lives only in the backend
// (src/config/phoneRules.js, served by GET /api/public/phone-rules) — edit
// it there; every field picks up the change on its next page load. The
// backend checks the same rule again (src/utils/mobileValidation.js).
//   • digits only (anything else is stripped as the user types)
//   • no leading 0 (except the few countries whose mobiles really start with 0)
//   • India: exactly 10 digits; other countries: their own length
//   • typing beyond the country's maximum is refused
// Default country everywhere: +91 (India).

import React, { useEffect, useState } from 'react';
import { publicAPI } from '../api';
import { useI18n } from '../i18n'; // English / Hindi messages (13 Oct 2026)

export const DEFAULT_COUNTRY_ISO = 'IN';
export const DEFAULT_DIAL_CODE = '+91';

// Built-in India rule so India fields work instantly, before the table loads.
const INDIA = { min: 10, max: 10, zeroAllowed: false };
const GENERIC = { min: 4, max: 15, zeroAllowed: false };
const FALLBACK = { byIso: { IN: { dial: '+91', ...INDIA } }, byDial: { '+91': INDIA } };

let cached = null;
let inflight = null;
function loadPhoneRules() {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = publicAPI.getPhoneRules()
      .then(r => { cached = (r && r.byIso && r.byDial) ? r : FALLBACK; return cached; })
      .catch(() => { inflight = null; return FALLBACK; });
  }
  return inflight;
}

// Hook: the rules table (fetched once per page load, shared by all fields).
export function usePhoneRules() {
  const [rules, setRules] = useState(cached || FALLBACK);
  useEffect(() => {
    let alive = true;
    loadPhoneRules().then(r => { if (alive) setRules(r); });
    return () => { alive = false; };
  }, []);
  return rules;
}

// Rule for a dial code ('+91') or ISO code ('IN').
export function phoneRuleFor(rules, dialOrIso) {
  if (!dialOrIso) return INDIA;
  const c = String(dialOrIso).trim();
  const r = (rules && (rules.byIso[c.toUpperCase()] || rules.byDial[c] || rules.byDial['+' + c.replace(/^\+/, '')]));
  if (r) return r;
  return (c === '+91' || c.toUpperCase() === 'IN') ? INDIA : GENERIC;
}

// Use in onChange: const r = takeMobileInput(e.target.value, current, rule);
// then store r.value and show r.error.
export function takeMobileInput(raw, current = '', rule = INDIA, label = 'Mobile number') {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length > rule.max) {
    return { value: String(current ?? '').slice(0, rule.max), error: `${label} cannot be more than ${rule.max} digits` };
  }
  if (digits.startsWith('0') && !rule.zeroAllowed) return { value: digits, error: `${label} cannot start with 0` };
  return { value: digits, error: '' };
}

// Use before submitting. Returns '' when the number is acceptable.
export function mobileSubmitError(value, rule = INDIA, label = 'Mobile number', required = true) {
  const n = String(value ?? '').trim();
  if (!n) return required ? `${label} is required` : '';
  if (!/^\d+$/.test(n)) return `${label} can contain digits only`;
  if (n.startsWith('0') && !rule.zeroAllowed) return `${label} cannot start with 0`;
  if (n.length > rule.max) return `${label} cannot be more than ${rule.max} digits`;
  if (n.length < rule.min) return rule.min === rule.max ? `${label} must be ${rule.min} digits` : `${label} must be ${rule.min}–${rule.max} digits`;
  return '';
}

// Placeholder text, e.g. "10-digit number" / "10–11-digit number".
export const mobilePlaceholder = (rule = INDIA) =>
  `${rule.min === rule.max ? rule.min : `${rule.min}–${rule.max}`}-digit number`;

// Country-code dropdown label, e.g. "+91 (India)".
export const dialLabel = (c) => `+${String(c.phonecode).replace(/^\+/, '')} (${c.name})`;

// Small red message under a mobile field.
// The message is shown in the page's language (Hindi patterns: i18n/hi.js).
export function MobileError({ message }) {
  const { t } = useI18n();
  if (!message) return null;
  return <div style={{ color: 'var(--red-600, #dc2626)', fontSize: 12, marginTop: 4 }}>{t(message)}</div>;
}
